import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAcademySlug } from '../../../../hooks/useAcademySlug';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from 'react-hot-toast';
import type { Booking, Student } from '@court-sync/shared';
import { isStudentProfileCompleteForBooking } from '@court-sync/shared';
import { useAuthStore } from '../../../../store/auth';
import { usePublicBookingStore } from '../../../../store/publicBooking';
import { isGroupDropInPublicBooking, isPerSessionPublicBooking, resolvePublicBookingSessionDatesToBook } from '../publicBookingSessionMode';
import { resolveGroupDropInSessionDate } from '../resolveGroupDropInSessionDate';
import { publicBookingService, CreateSocialBookingData } from '../../../../services/publicBooking';
import { eoiService } from '../../../../services/eoi';
import { studentService } from '../../../../services/student';
import { waitlistService } from '../../../../services/waitlist';
import { isLessonFull } from '../../../../utils/lessonBooking';
import { studentFormSchema, type StudentFormData, buildEmergencyContactFromStudentForm } from '../schemas';
import { academyProfileService } from '@/services/academyProfile';
import { PublicBookingStudentFormStep as StudentForm } from '../PublicBookingStudentFormStep';
import { useOpenAcademyBookingLogin } from '@/hooks/useOpenAcademyBookingLogin';
import { appendPublicBookingWizardContextQueries, publicBookingStepPath } from '../bookingPaths';
import type { PublicBookingWizardSegment } from '../bookingPaths';
import type { AuthenticatedUser } from '../types';
import { Button } from '@/components/ui';
import { User, UserPlus } from 'lucide-react';
import type { WaitlistGuestStudentPayload } from '../../../../services/waitlist';

function perSessionSelectionErrorMessage(lessonType: string | undefined): string {
  return lessonType === 'camp'
    ? 'Please select at least one camp day.'
    : 'Please select at least one session.';
}

function socialBookingErrorMessage(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { error?: string } }; message?: string };
  return ax.response?.data?.error || (err instanceof Error ? err.message : fallback);
}

function userToAuthenticated(u: { id: string; email?: string | null; firstName?: string; lastName?: string } | null): AuthenticatedUser | null {
  if (!u) return null;
  return {
    id: u.id,
    email: u.email ?? null,
    firstName: u.firstName,
    lastName: u.lastName,
  };
}

export function BookingDetailsStep() {
  const academySlug = useAcademySlug();
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const openBookingLogin = useOpenAcademyBookingLogin();
  const { user: authStoreUser, refreshUser } = useAuthStore();

  const academy = usePublicBookingStore((s) => s.academy);
  const lesson = usePublicBookingStore((s) => s.lesson);
  const userType = usePublicBookingStore((s) => s.userType);
  const selectedSessionDates = usePublicBookingStore((s) => s.selectedSessionDates);
  const campBookingMode = usePublicBookingStore((s) => s.campBookingMode);
  const groupBookingMode = usePublicBookingStore((s) => s.groupBookingMode);
  const waitlistClaimEntryId = usePublicBookingStore((s) => s.waitlistClaimEntryId);
  const invitationToken = usePublicBookingStore((s) => s.invitationToken);
  const manualInviteToken = usePublicBookingStore((s) => s.manualInviteToken);
  const hasPaymentProcessing = usePublicBookingStore((s) => s.hasPaymentProcessing);
  const setBooking = usePublicBookingStore((s) => s.setBooking);
  const setSelectedSessionDates = usePublicBookingStore((s) => s.setSelectedSessionDates);
  const fetchExistingStudents = usePublicBookingStore((s) => s.fetchExistingStudents);
  const fetchWaitlistData = usePublicBookingStore((s) => s.fetchWaitlistData);
  const fetchLessonStudentEnrollments = usePublicBookingStore((s) => s.fetchLessonStudentEnrollments);
  const lessonOverrides = usePublicBookingStore((s) => s.lessonOverrides);
  const lessonStudentEnrollments = usePublicBookingStore((s) => s.lessonStudentEnrollments);
  const term = usePublicBookingStore((s) => s.term);
  const existingStudents = usePublicBookingStore((s) => s.existingStudents);
  const selfStudent = usePublicBookingStore((s) => s.selfStudent);
  const waitlistStudents = usePublicBookingStore((s) => s.waitlistStudents);
  const myWaitlistEntries = usePublicBookingStore((s) => s.myWaitlistEntries);
  const setMyWaitlistEntries = usePublicBookingStore((s) => s.setMyWaitlistEntries);

  const [studentFormSubmitting, setStudentFormSubmitting] = useState(false);
  const [joinWaitlistLoading, setJoinWaitlistLoading] = useState(false);
  /** Waitlist: pick existing vs form for a new child or for self. */
  const [waitlistPickerMode, setWaitlistPickerMode] = useState<'list' | 'form_child' | 'form_myself'>('list');

  const studentForm = useForm<StudentFormData>({
    resolver: zodResolver(studentFormSchema) as Resolver<StudentFormData>,
    defaultValues: {
      bookingFor: 'child',
      useCustomerAsEmergencyContact: true,
    },
  });

  const isPerSessionBooking = isPerSessionPublicBooking(
    lesson,
    campBookingMode,
    groupBookingMode
  );
  const isGroupDropIn = isGroupDropInPublicBooking(lesson, groupBookingMode);

  const sessionDatesToBookForCheckout = useCallback(
    () =>
      resolvePublicBookingSessionDatesToBook(
        lesson,
        campBookingMode,
        groupBookingMode,
        selectedSessionDates
      ),
    [lesson, campBookingMode, groupBookingMode, selectedSessionDates]
  );

  const prepareDropInSessionForStudent = useCallback(
    (studentId: string): string | null => {
      if (!isGroupDropIn || !lesson) return selectedSessionDates[0] ?? null;
      const enrollment = lessonStudentEnrollments.find((e) => e.studentId === studentId);
      if (enrollment?.hasFullTerm) {
        toast.error('This student already has a full-term booking for this lesson.');
        return null;
      }
      const excluded = enrollment?.sessionDates ?? [];
      const next = resolveGroupDropInSessionDate(lesson, term, excluded, lessonOverrides);
      if (!next) {
        toast.error('No upcoming sessions are available for drop-in booking for this student.');
        setSelectedSessionDates([]);
        return null;
      }
      setSelectedSessionDates([next]);
      return next;
    },
    [
      isGroupDropIn,
      lesson,
      term,
      lessonOverrides,
      lessonStudentEnrollments,
      selectedSessionDates,
      setSelectedSessionDates,
    ]
  );

  const authenticatedUser = useMemo((): AuthenticatedUser | null => {
    return userToAuthenticated(authStoreUser);
  }, [authStoreUser]);

  const navigateBookingStep = useCallback(
    (segment: PublicBookingWizardSegment, navOpts?: { replace?: boolean }) => {
      if (!academySlug || !lessonId) return;
      navigate(
        appendPublicBookingWizardContextQueries(
          publicBookingStepPath(academySlug, lessonId, segment),
          { invitationToken, manualInviteToken }
        ),
        navOpts
      );
    },
    [navigate, academySlug, lessonId, invitationToken, manualInviteToken]
  );

  useEffect(() => {
    if (!userType || !authStoreUser) {
      navigateBookingStep('register', { replace: true });
    }
  }, [userType, authStoreUser, navigateBookingStep]);

  useEffect(() => {
    if (academy?.id && authStoreUser) {
      fetchExistingStudents(academy.id, authStoreUser);
    }
  }, [academy?.id, authStoreUser, fetchExistingStudents]);

  useEffect(() => {
    if (isGroupDropIn && lesson?.id && authStoreUser) {
      void fetchLessonStudentEnrollments(lesson.id);
    }
  }, [isGroupDropIn, lesson?.id, authStoreUser, fetchLessonStudentEnrollments]);

  // Invitation-only: redirect to EOI unless waitlist claim, invitation URL, or program membership
  useEffect(() => {
    if (!lesson?.isInvitationOnly || !academy?.id || !lesson.id || !academySlug || !lessonId || !authStoreUser) {
      return undefined;
    }
    if (invitationToken || waitlistClaimEntryId) {
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const studentsRes = await studentService.getStudents(academy.id);
        const ids = (studentsRes.data || []).map((s) => s.id);
        if (ids.length === 0) return;
        const access = await eoiService.getProgramAccess(lesson.id, academy.id, ids);
        if (cancelled) return;
        if (access.success && access.data?.canBookDirectly) return;
        navigateBookingStep('eoi', { replace: true });
      } catch {
        /* stay on details */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    lesson?.isInvitationOnly,
    lesson?.id,
    academy?.id,
    academySlug,
    lessonId,
    authStoreUser,
    invitationToken,
    waitlistClaimEntryId,
    navigateBookingStep,
  ]);

  const isLessonFullNow = lesson && isLessonFull(lesson, academy?.allowEnrolmentWhenFull);
  const showWaitlistUI =
    !!isLessonFullNow &&
    !academy?.allowEnrolmentWhenFull &&
    !!academy?.waitlistEnabled &&
    !!lesson?.termId;
  const onWaitlistForLesson = !!lesson && myWaitlistEntries.some((e) => e.lessonId === lesson.id);
  const hasSelfStudentInList = waitlistStudents.some((s) => s.isSelf);

  useEffect(() => {
    if (!showWaitlistUI) return;
    if (!authStoreUser?.id || !academy?.id) return;
    fetchWaitlistData(academy.id, authStoreUser.id);
  }, [showWaitlistUI, authStoreUser, academy?.id, fetchWaitlistData]);

  useEffect(() => {
    if (!showWaitlistUI) {
      setWaitlistPickerMode('list');
    }
  }, [showWaitlistUI]);

  useEffect(() => {
    if (waitlistPickerMode !== 'form_child' && waitlistPickerMode !== 'form_myself') return;
    const user = authStoreUser;
    if (!user) return;
    if (waitlistPickerMode === 'form_child') {
      studentForm.reset({
        bookingFor: 'child',
        firstName: '',
        lastName: '',
        dateOfBirth: '',
        gender: undefined,
        skillLevel: undefined,
        medicalNotes: '',
        customerFirstName: user.firstName ?? '',
        customerLastName: user.lastName ?? '',
        customerEmail: user.email ?? '',
        useCustomerAsEmergencyContact: true,
        emergencyContact: {
          name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          phone: '',
          relationship: 'Parent/Guardian',
        },
      });
    } else {
      studentForm.reset({
        bookingFor: 'myself',
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        dateOfBirth: '',
        gender: undefined,
        skillLevel: 'beginner',
        medicalNotes: '',
        customerFirstName: user.firstName ?? '',
        customerLastName: user.lastName ?? '',
        customerEmail: user.email ?? '',
        useCustomerAsEmergencyContact: true,
        emergencyContact: {
          name: `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim(),
          phone: '',
          relationship: 'Parent/Guardian',
        },
      });
    }
  }, [waitlistPickerMode, authStoreUser, studentForm]);

  const handleSelectMyselfForWaitlist = async () => {
    if (!selfStudent?.id) return;
    await handleJoinWaitlist(selfStudent.id);
    setWaitlistPickerMode('list');
  };

  const handleWaitlistMyselfClick = () => {
    if (selfStudent?.id && isStudentProfileCompleteForBooking(selfStudent)) {
      void handleJoinWaitlist(selfStudent.id);
      return;
    }
    setWaitlistPickerMode('form_myself');
  };
  const handleJoinWaitlist = async (studentId: string) => {
    if (!lesson || !academy || !lesson.termId) return;
    try {
      setJoinWaitlistLoading(true);
      const res = await waitlistService.joinWaitlist({
        lessonId: lesson.id,
        termId: lesson.termId,
        studentId,
        academyId: academy.id,
      });
      if (res.success) {
        toast.success("You're on the waitlist");
        setMyWaitlistEntries((prev) => [
          ...prev,
          { lessonId: lesson.id, id: (res.data as { waitlistEntry?: { id?: string } })?.waitlistEntry?.id ?? '' },
        ]);
      } else {
        toast.error((res as { error?: string }).error || 'Failed to join waitlist');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(ax.response?.data?.error || ax.message || 'Failed to join waitlist');
    } finally {
      setJoinWaitlistLoading(false);
    }
  };

  const handleWaitlistStudentFormSubmit = async (data: StudentFormData) => {
    if (!lesson || !academy || !lesson.termId || !authStoreUser?.id) return;
    try {
      setStudentFormSubmitting(true);
      const studentData: WaitlistGuestStudentPayload = {
        firstName:
          data.bookingFor === 'myself' ? (data.customerFirstName ?? '').trim() : (data.firstName ?? '').trim(),
        lastName:
          data.bookingFor === 'myself' ? (data.customerLastName ?? '').trim() : (data.lastName ?? '').trim(),
        dateOfBirth: data.dateOfBirth ?? '',
        gender: data.gender!,
        ...(data.skillLevel ? { skillLevel: data.skillLevel } : {}),
        medicalNotes: data.medicalNotes?.trim() || undefined,
        emergencyContact: buildEmergencyContactFromStudentForm(data),
        isSelf: data.bookingFor === 'myself',
      };
      const res = await waitlistService.joinWaitlist({
        lessonId: lesson.id,
        termId: lesson.termId,
        academyId: academy.id,
        studentData,
        customerFirstName: data.customerFirstName,
        customerLastName: data.customerLastName,
        customerEmail: data.customerEmail,
      });
      if (res.success) {
        toast.success("You're on the waitlist");
        setMyWaitlistEntries((prev) => [
          ...prev,
          { lessonId: lesson.id, id: (res.data as { waitlistEntry?: { id?: string } })?.waitlistEntry?.id ?? '' },
        ]);
        setWaitlistPickerMode('list');
        await fetchWaitlistData(academy.id, authStoreUser.id);
        await fetchExistingStudents(academy.id, authStoreUser);
      } else {
        toast.error((res as { error?: string }).error || 'Failed to join waitlist');
      }
    } catch (err: unknown) {
      const ax = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(ax.response?.data?.error || ax.message || 'Failed to join waitlist');
    } finally {
      setStudentFormSubmitting(false);
    }
  };

  const handleSelectMyself = async () => {
    if (!authStoreUser?.id || !lesson || !academy || !academySlug || !lessonId) {
      toast.error('Unable to complete booking. Please try again.');
      return;
    }
    if (selfStudent?.id && isStudentProfileCompleteForBooking(selfStudent)) {
      await handleSelectExistingStudent(selfStudent);
      return;
    }
    if (isPerSessionBooking && selectedSessionDates.length === 0) {
      toast.error(
        isGroupDropIn
          ? 'No upcoming sessions are available for drop-in booking.'
          : perSessionSelectionErrorMessage(lesson.lessonType)
      );
      if (!isGroupDropIn) {
        navigateBookingStep('sessions');
      }
      return;
    }
    if (isGroupDropIn && selfStudent?.id) {
      const next = prepareDropInSessionForStudent(selfStudent.id);
      if (!next) return;
    }
    try {
      setStudentFormSubmitting(true);
      const firstName = authStoreUser.firstName ?? '';
      const lastName = authStoreUser.lastName ?? '';
      const email = authStoreUser.email ?? '';
      const sessionDatesToBook = sessionDatesToBookForCheckout();
      const createdBookings: Booking[] = [];
      for (const sessionDate of sessionDatesToBook) {
        const socialBookingData: CreateSocialBookingData = {
          lessonId: lesson.id,
          termId: lesson.termId ?? undefined,
          academyId: academy.id,
          studentData:
            createdBookings.length > 0
              ? undefined
              : {
                  firstName,
                  lastName,
                  dateOfBirth: new Date('1990-01-01'),
                  gender: 'other',
                  skillLevel: 'beginner',
                },
          studentId: createdBookings.length > 0 ? createdBookings[0].studentId : undefined,
          bookingFor: 'myself',
          userId: authStoreUser.id,
          customerFirstName: firstName,
          customerLastName: lastName,
          customerEmail: email,
          ...(waitlistClaimEntryId && { waitlistEntryId: waitlistClaimEntryId }),
          ...(sessionDate ? { sessionDate } : {}),
          ...(invitationToken && { invitationToken }),
          ...(manualInviteToken?.trim() && { manualInviteToken: manualInviteToken.trim() }),
        };
        const bookingResponse = await publicBookingService.createSocialBooking(socialBookingData);
        createdBookings.push(bookingResponse.data.booking);
      }
      const b = createdBookings[0];
      setBooking(b, createdBookings);

      refreshUser().catch(() => {});

      const bookingAmount =
        createdBookings.reduce((sum, x) => sum + (x.totalAmount ?? 0), 0) || lesson?.price || 0;
      const isFreeLesson = bookingAmount <= 0;

      if (!hasPaymentProcessing) {
        setBooking(
          { ...b, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((x) => ({ ...x, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else if (b.paymentStatus === 'paid') {
        toast.success('You already have a booking for this lesson.');
        navigateBookingStep('success');
      } else if (isFreeLesson) {
        setBooking(
          { ...b, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((x) => ({ ...x, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else {
        toast.success('Booking created! Please proceed to payment.');
        navigateBookingStep('payment');
      }
    } catch (err: unknown) {
      toast.error(socialBookingErrorMessage(err, 'Failed to create booking'));
    } finally {
      setStudentFormSubmitting(false);
    }
  };

  const handleSelectExistingStudent = async (student: Student) => {
    if (!authStoreUser?.id || !lesson || !academy || !academySlug || !lessonId) {
      toast.error('Unable to complete booking. Please try again.');
      return;
    }
    if (isPerSessionBooking && selectedSessionDates.length === 0) {
      toast.error(
        isGroupDropIn
          ? 'No upcoming sessions are available for drop-in booking.'
          : perSessionSelectionErrorMessage(lesson.lessonType)
      );
      if (!isGroupDropIn) {
        navigateBookingStep('sessions');
      }
      return;
    }
    if (isGroupDropIn) {
      const next = prepareDropInSessionForStudent(student.id);
      if (!next) return;
    }
    try {
      setStudentFormSubmitting(true);
      const sessionDatesToBook = sessionDatesToBookForCheckout();
      const createdBookings: Booking[] = [];
      for (const sessionDate of sessionDatesToBook) {
        const socialBookingData: CreateSocialBookingData = {
          lessonId: lesson.id,
          termId: lesson.termId ?? undefined,
          academyId: academy.id,
          studentId: student.id,
          userId: authStoreUser.id,
          customerFirstName: authStoreUser.firstName ?? '',
          customerLastName: authStoreUser.lastName ?? '',
          customerEmail: authStoreUser.email ?? '',
          ...(waitlistClaimEntryId && { waitlistEntryId: waitlistClaimEntryId }),
          ...(sessionDate ? { sessionDate } : {}),
          ...(invitationToken && { invitationToken }),
        };
        const bookingResponse = await publicBookingService.createSocialBooking(socialBookingData);
        createdBookings.push(bookingResponse.data.booking);
      }
      const b = createdBookings[0];
      setBooking(b, createdBookings);

      refreshUser().catch(() => {});

      const bookingAmount =
        createdBookings.reduce((sum, x) => sum + (x.totalAmount ?? 0), 0) || lesson?.price || 0;
      const isFreeLesson = bookingAmount <= 0;

      if (!hasPaymentProcessing) {
        setBooking(
          { ...b, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((x) => ({ ...x, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else if (b.paymentStatus === 'paid') {
        toast.success('You already have a booking for this lesson.');
        navigateBookingStep('success');
      } else if (isFreeLesson) {
        setBooking(
          { ...b, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((x) => ({ ...x, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else {
        toast.success('Booking created! Please proceed to payment.');
        navigateBookingStep('payment');
      }
    } catch (err: unknown) {
      toast.error(socialBookingErrorMessage(err, 'Failed to create booking'));
    } finally {
      setStudentFormSubmitting(false);
    }
  };

  const handleStudentFormSubmit = async (data: StudentFormData) => {
    if (!lesson || !academy || !academySlug || !lessonId) return;
    if (!authStoreUser || !authStoreUser.id) {
      toast.error('You must be logged in to complete your booking. Please sign in and try again.');
      openBookingLogin(academySlug, lessonId, {
        invitationToken,
        manualInviteToken,
        waitlistEntryId: waitlistClaimEntryId,
      });
      return;
    }
    try {
      setStudentFormSubmitting(true);

      if (isPerSessionBooking && selectedSessionDates.length === 0) {
        toast.error(perSessionSelectionErrorMessage(lesson.lessonType));
        navigateBookingStep('sessions');
        return;
      }

      const studentData =
        data.bookingFor === 'myself'
          ? {
              firstName: data.customerFirstName,
              lastName: data.customerLastName,
              dateOfBirth: new Date(data.dateOfBirth!),
              gender: data.gender! as 'male' | 'female' | 'other',
              ...(data.skillLevel ? { skillLevel: data.skillLevel } : {}),
              medicalNotes: data.medicalNotes || undefined,
              emergencyContact: buildEmergencyContactFromStudentForm(data),
            }
          : {
              firstName: data.firstName!,
              lastName: data.lastName!,
              dateOfBirth: new Date(data.dateOfBirth!),
              gender: data.gender!,
              ...(data.skillLevel ? { skillLevel: data.skillLevel } : {}),
              medicalNotes: data.medicalNotes || undefined,
              emergencyContact: buildEmergencyContactFromStudentForm(data),
            };

      const sessionDatesToBook = sessionDatesToBookForCheckout();

      const createdBookings: Booking[] = [];

      for (const sessionDate of sessionDatesToBook) {
        const socialBookingData: CreateSocialBookingData = {
          lessonId: lesson.id,
          termId: lesson.termId,
          academyId: academy.id,
          studentData: createdBookings.length > 0 ? undefined : studentData,
          studentId: createdBookings.length > 0 ? createdBookings[0].studentId : undefined,
          bookingFor: data.bookingFor,
          userId: authStoreUser.id,
          customerFirstName: data.customerFirstName,
          customerLastName: data.customerLastName,
          customerEmail: data.customerEmail,
          ...(waitlistClaimEntryId && { waitlistEntryId: waitlistClaimEntryId }),
          ...(sessionDate ? { sessionDate } : {}),
          ...(invitationToken && { invitationToken }),
          ...(manualInviteToken?.trim() && { manualInviteToken: manualInviteToken.trim() }),
        };
        const bookingResponse = await publicBookingService.createSocialBooking(socialBookingData);
        createdBookings.push(bookingResponse.data.booking);
      }

      const primary = createdBookings[0];
      setBooking(primary, createdBookings);

      refreshUser().catch(() => {});

      if (data.smsOptIn && data.customerPhone?.trim() && academySlug) {
        academyProfileService
          .updateSmsPreferences(academySlug, {
            smsConsent: true,
            phone: data.customerPhone.trim(),
          })
          .catch(() => undefined);
      }

      const bookingAmount =
        createdBookings.reduce((sum, b) => sum + (b.totalAmount ?? 0), 0) || lesson.price || 0;
      const isFreeLesson = bookingAmount <= 0;

      if (!hasPaymentProcessing) {
        setBooking(
          { ...primary, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((b) => ({ ...b, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else if (primary.paymentStatus === 'paid') {
        toast.success('You already have a booking for this lesson.');
        navigateBookingStep('success');
      } else if (isFreeLesson) {
        setBooking(
          { ...primary, status: 'confirmed', paymentStatus: 'pending' },
          createdBookings.map((b) => ({ ...b, status: 'confirmed' as const, paymentStatus: 'pending' as const }))
        );
        toast.success('Booking confirmed successfully!');
        navigateBookingStep('success');
      } else {
        toast.success('Booking created successfully! Please proceed to payment.');
        navigateBookingStep('payment');
      }
    } catch (err: unknown) {
      toast.error(socialBookingErrorMessage(err, 'Failed to create booking'));
    } finally {
      setStudentFormSubmitting(false);
    }
  };

  if (!lesson || !academy) {
    return null;
  }

  if (showWaitlistUI) {
    if (authStoreUser && !onWaitlistForLesson && (waitlistPickerMode === 'form_child' || waitlistPickerMode === 'form_myself')) {
      return (
        <StudentForm
          form={studentForm}
          onSubmit={handleWaitlistStudentFormSubmit}
          isSubmitting={studentFormSubmitting || joinWaitlistLoading}
          authenticatedUser={authenticatedUser}
          authStoreUser={userToAuthenticated(authStoreUser)}
          hasPaymentProcessing={hasPaymentProcessing}
          existingStudents={[]}
          selfStudent={selfStudent}
          lockBookingFor={waitlistPickerMode === 'form_child' ? 'child' : 'myself'}
          onSelectMyself={
            waitlistPickerMode === 'form_myself' && authenticatedUser
              ? handleSelectMyselfForWaitlist
              : undefined
          }
          variant="waitlist"
          onBackFromWaitlistForm={() => setWaitlistPickerMode('list')}
          defaultCountrySource={{
            country: academy.country,
            region: academy.region,
          }}
        />
      );
    }

    return (
      <div className="space-y-6">
        {!authStoreUser ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground mb-4">
              Sign in to join the waitlist for this lesson. When a spot opens, you&apos;ll be notified.
            </p>
            <Button
              onClick={() => {
                openBookingLogin(academySlug!, lessonId!, {
                  invitationToken,
                  manualInviteToken,
                  waitlistEntryId: waitlistClaimEntryId,
                });
              }}
            >
              Sign in to join waitlist
            </Button>
          </div>
        ) : onWaitlistForLesson ? (
          <div className="text-center py-8">
            <p className="text-green-600 dark:text-green-400 font-medium mb-2">You&apos;re on the waitlist</p>
            <p className="text-muted-foreground text-sm">
              When a spot opens, you&apos;ll receive an email with a link to claim it.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-foreground">Join the waitlist</h3>
            <p className="text-muted-foreground text-sm">Select who you&apos;d like to join the waitlist for:</p>
            <div className="grid gap-3 sm:grid-cols-2">
              {waitlistStudents.map((student) => (
                <button
                  key={student.id}
                  type="button"
                  onClick={() => handleJoinWaitlist(student.id)}
                  disabled={joinWaitlistLoading}
                  className="flex items-center p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700 text-left transition-colors disabled:opacity-50"
                >
                  <User className="h-5 w-5 text-blue-600 mr-3 shrink-0" />
                  <span className="font-medium text-foreground">
                    {student.firstName} {student.lastName}
                    {student.isSelf && <span className="ml-1 text-sm text-muted-foreground">(Myself)</span>}
                  </span>
                </button>
              ))}
              <button
                type="button"
                onClick={() => setWaitlistPickerMode('form_child')}
                disabled={joinWaitlistLoading}
                className="flex items-center p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 border-dashed border-gray-300 dark:border-gray-600 text-left transition-colors disabled:opacity-50"
              >
                <UserPlus className="h-5 w-5 text-blue-600 mr-3 shrink-0" />
                <span className="font-medium text-foreground">A new student</span>
              </button>
              {!hasSelfStudentInList && (
                <button
                  type="button"
                  onClick={handleWaitlistMyselfClick}
                  disabled={joinWaitlistLoading}
                  className="flex items-center p-4 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 border-dashed border-gray-300 dark:border-gray-600 text-left transition-colors disabled:opacity-50"
                >
                  <User className="h-5 w-5 text-blue-600 mr-3 shrink-0" />
                  <span className="font-medium text-foreground">Myself</span>
                </button>
              )}
            </div>
            {joinWaitlistLoading && <p className="text-sm text-muted-foreground">Adding to waitlist...</p>}
          </div>
        )}
      </div>
    );
  }

  return (
    <StudentForm
      form={studentForm}
      onSubmit={handleStudentFormSubmit}
      isSubmitting={studentFormSubmitting}
      authenticatedUser={authenticatedUser}
      authStoreUser={userToAuthenticated(authStoreUser)}
      hasPaymentProcessing={hasPaymentProcessing}
      existingStudents={existingStudents}
      selfStudent={selfStudent}
      onSelectExistingStudent={authenticatedUser ? handleSelectExistingStudent : undefined}
      onSelectMyself={authenticatedUser ? handleSelectMyself : undefined}
      defaultCountrySource={{
        country: academy.country,
        region: academy.region,
      }}
    />
  );
}
